import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import StatusBadge from '../components/StatusBadge';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Wrench, ArrowUpRight } from 'lucide-react';

const COLUMNS = [
  { key: 'new', label: 'New', dot: 'bg-blue-500' },
  { key: 'scheduled', label: 'Scheduled', dot: 'bg-indigo-500' },
  { key: 'assigned', label: 'Assigned', dot: 'bg-violet-500' },
  { key: 'in_progress', label: 'In Progress', dot: 'bg-amber-500' },
  { key: 'waiting_parts', label: 'Waiting on Parts', dot: 'bg-orange-500' },
  { key: 'completed', label: 'Completed', dot: 'bg-emerald-500' },
  { key: 'cancelled', label: 'Cancelled', dot: 'bg-slate-400' },
];

export default function ServiceTickets() {
  const { tickets, technicians, updateTicketStatus } = useApp();
  const navigate = useNavigate();

  const byCol = useMemo(() => {
    const out = {};
    COLUMNS.forEach((c) => (out[c.key] = []));
    tickets.forEach((t) => { if (out[t.status]) out[t.status].push(t); });
    return out;
  }, [tickets]);

  const techName = (id) => technicians.find((t) => t.id === id)?.name || '—';

  const advanceTicket = (e, ticket) => {
    e.stopPropagation();
    const idx = COLUMNS.findIndex((c) => c.key === ticket.status);
    const next = COLUMNS[Math.min(idx + 1, COLUMNS.length - 2)];
    if (next) updateTicketStatus(ticket.id, next.key);
  };

  return (
    <div className="space-y-5" data-testid="tickets-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Service Tickets</div>
          <h1 className="mt-1 text-3xl font-semibold text-slate-900 tracking-tight">Work order board</h1>
          <p className="mt-1 text-sm text-slate-500">Track diagnostic-driven service tickets across technicians.</p>
        </div>
        <Button className="bg-[#0052CC] hover:bg-[#0043A6] h-9" data-testid="ticket-new-btn">
          <Plus className="h-4 w-4 mr-2" /> New Ticket
        </Button>
      </div>

      <div className="overflow-x-auto pb-3">
        <div className="flex gap-4 min-w-max">
          {COLUMNS.map((col) => (
            <div key={col.key} className="w-80 shrink-0" data-testid={`kanban-col-${col.key}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${col.dot}`} />
                  <div className="text-sm font-semibold text-slate-900">{col.label}</div>
                  <span className="font-mono text-xs text-slate-500">{byCol[col.key].length}</span>
                </div>
              </div>
              <div className="bg-slate-100/70 rounded-lg p-2 min-h-[200px] space-y-2">
                {byCol[col.key].map((t) => (
                  <Card
                    key={t.id}
                    className="p-3 border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-[1px] transition-all cursor-pointer bg-white"
                    onClick={() => navigate(`/tickets/${t.id}`)}
                    data-testid={`ticket-card-${t.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-[11px] text-slate-500">{t.id}</div>
                      <StatusBadge status={t.priority === 'High' ? 'critical' : t.priority === 'Medium' ? 'warning' : 'info'}>{t.priority}</StatusBadge>
                    </div>
                    <div className="mt-2 text-sm font-semibold text-slate-900 leading-snug">{t.issue}</div>
                    <div className="mt-0.5 text-xs text-slate-600">{t.customerName}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{t.address}</div>
                    <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                      <div className="text-[11px] text-slate-500">
                        <Wrench className="h-3 w-3 inline -mt-0.5 mr-1" />{techName(t.technician)}
                      </div>
                      <div className="font-mono text-xs font-semibold text-slate-900">${t.estRevenue}</div>
                    </div>
                    <div className="mt-1.5 text-[11px] font-mono text-slate-500">{t.scheduledAt}</div>
                    {col.key !== 'completed' && col.key !== 'cancelled' && (
                      <Button size="sm" variant="ghost" className="h-7 w-full mt-2 text-xs justify-center" onClick={(e) => advanceTicket(e, t)}>
                        Advance <ArrowUpRight className="h-3 w-3 ml-1" />
                      </Button>
                    )}
                  </Card>
                ))}
                {byCol[col.key].length === 0 && (
                  <div className="text-center py-8 text-xs text-slate-400">No tickets</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
